import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchProductsByUser } from '../utils/apiHelpers';
import FallbackImage from '../components/FallbackImage';
import type { Product } from '../utils/apiHelpers';
import ProductGrid from '../components/ProductGrid';

interface VendorInfo {
  id: number;
  store_name: string;
  address?: string;
  phone?: string;
  rating?: string;
  total_earnings?: string;
  business_info?: string;
  website?: string;
  store_profile_image?: string;
  store_banner?: string;
}

export default function VendorPage() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const [vendorInfo, setVendorInfo] = useState<VendorInfo | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (!vendorId) return;
    
    setLoading(true);
    setProductsLoading(true);
    
    // Fetch all products for this vendor from API by user ID
    fetchProductsByUser(Number(vendorId))
      .then((list) => {
        setProducts(list);
        
        // Extract vendor info from the first product if available
        if (list.length > 0) {
          const firstProduct = list[0];
          const productVendorName = firstProduct.vendor_name;
          
          if (productVendorName) {
            // Extract vendor info from the first product
            const vendorDetails = (firstProduct.extra as any)?.vendor_details;
            
            if (vendorDetails) {
              const vendorData: VendorInfo = {
                id: vendorDetails.id || Number(vendorId),
                store_name: vendorDetails.store_name || productVendorName,
                address: vendorDetails.address,
                phone: vendorDetails.phone,
                rating: vendorDetails.rating,
                total_earnings: vendorDetails.total_earnings,
                business_info: vendorDetails.business_info,
                website: vendorDetails.website,
                store_profile_image: vendorDetails.store_profile_image,
                store_banner: vendorDetails.store_banner,
              };
              
              console.log('Vendor name:', productVendorName);
              console.log('Vendor info from product:', vendorData);
              console.log('Products count:', list.length);
              setVendorInfo(vendorData);
              
              // Also store in localStorage for faster loading next time
              localStorage.setItem(`vendor_${vendorId}`, JSON.stringify(vendorData));
            } else {
              // Fallback: create basic vendor info from product data
              const vendorData: VendorInfo = {
                id: Number(vendorId),
                store_name: productVendorName,
              };
              setVendorInfo(vendorData);
              localStorage.setItem(`vendor_${vendorId}`, JSON.stringify(vendorData));
            }
          }
        }
      })
      .catch((error) => {
        console.error('Failed to fetch vendor products:', error);
        setProducts([]);
      })
      .finally(() => {
        setLoading(false);
        setProductsLoading(false);
      });
  }, [vendorId]);

  if (loading) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse h-96 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </main>
    );
  }

  if (!vendorId || !vendorInfo) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-gray-500">Vendor not found.</p>
        <Link to="/shop" className="text-green-600 hover:underline mt-2 inline-block">
          Back to shop
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-1">/</span>
        <Link to="/shop" className="hover:underline">Shop</Link>
        <span className="mx-1">/</span>
        <span className="text-gray-700">{vendorInfo.store_name}</span>
      </nav>

      {/* Vendor Banner */}
      {vendorInfo.store_banner && (
        <div className="mb-8 rounded-2xl overflow-hidden bg-gray-200 h-64">
          <FallbackImage
            src={vendorInfo.store_banner}
            alt={vendorInfo.store_name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Vendor Info Section */}
      <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm mb-8">
        <div className="flex items-start gap-6">
          {vendorInfo.store_profile_image && (
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
              <FallbackImage
                src={vendorInfo.store_profile_image}
                alt={vendorInfo.store_name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-[var(--foreground)] mb-2">
              {vendorInfo.store_name}
            </h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {vendorInfo.rating && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Rating:</span> {vendorInfo.rating}
                </p>
              )}
              {vendorInfo.total_earnings && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Total Earnings:</span> Rs {vendorInfo.total_earnings}
                </p>
              )}
              {vendorInfo.phone && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Phone:</span> {vendorInfo.phone}
                </p>
              )}
              {vendorInfo.website && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Website:</span> <a href={vendorInfo.website} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">{vendorInfo.website}</a>
                </p>
              )}
              {vendorInfo.address && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Address:</span> {vendorInfo.address}
                </p>
              )}
            </div>

            {vendorInfo.business_info && (
              <p className="text-sm text-gray-600 mb-4">
                {vendorInfo.business_info}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Products Section */}
      <section>
        <h2 className="text-2xl font-semibold text-[var(--foreground)] mb-6">
          Products from {vendorInfo.store_name}
        </h2>
        <ProductGrid products={products} loading={productsLoading} />
        
        {!productsLoading && products.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-12">
            No products found from this vendor.
          </p>
        )}
      </section>
    </main>
  );
}
